/**
 * React-Hooks fuer Kundenportal-Tracking.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from './trackingClient';

/**
 * Feuert ein page_view-Event bei jeder Navigation.
 * Mounten in der Root-Komponente des Kundenportals.
 */
export function useTrackPageView(): void {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;
    trackEvent({ type: 'page_view', path });
  }, [location]);
}

/**
 * Zeit-auf-Seite-Tracking: feuert bei Unmount oder Visibility-Change.
 * `path` kann ueberschrieben werden; sonst aktueller Pathname.
 */
export function useTimeOnPage(path?: string): void {
  const location = useLocation();
  useEffect(() => {
    const startedAt = Date.now();
    const pagePath = path ?? location.pathname;

    const emit = () => {
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      if (seconds < 1) return;
      trackEvent({ type: 'time_on_page', path: pagePath, value: seconds });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') emit();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      emit();
    };
  }, [location.pathname, path]);
}

/**
 * Scroll-Tiefe-Tracking: emittiert 25/50/75/100%-Meilensteine ein Mal pro Seite.
 */
export function useScrollDepth(): void {
  const location = useLocation();
  useEffect(() => {
    const milestones = [25, 50, 75, 100];
    const fired = new Set<number>();

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.floor((scrollTop / docHeight) * 100);
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          trackEvent({ type: 'scroll', path: location.pathname, value: m });
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);
}

/**
 * Imperatives Click-Tracking. Nutzen via:
 *   const trackClick = useTrackClick();
 *   <Button onClick={() => { trackClick('create-ticket'); handleClick(); }}>…
 */
export function useTrackClick(): (
  target: string,
  metadata?: Record<string, unknown>
) => void {
  const location = useLocation();
  return (target, metadata) => {
    trackEvent({
      type: 'click',
      target,
      path: location.pathname,
      ...(metadata ? { metadata } : {}),
    });
  };
}
